import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import '@adornis/file-utils/client/x-image.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { syncEmailBannerToTemplates } from '../../../api/company-registration.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { MatchingWeekSettings } from '../../../db/settings.js';
import { formatFormError } from '../../components/format-form-error.js';

export const AdminEmailSettings = stateful(() => {
  const [saving, setSaving] = useState(false, []);
  const [saveSuccess, setSaveSuccess] = useState(false, []);
  const [saveError, setSaveError] = useState<string | null>(null, []);

  const settingsQuery = useQuery(
    () =>
      subscribeMatchingWeekSettings()({
        _id: 1,
        emailBannerFileId: 1,
        adminNotificationEmail: 1,
      }),
    [],
  );

  const handleBannerUpload = async (fileId: string | null) => {
    if (!settingsQuery.value) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      settingsQuery.value.emailBannerFileId = fileId;
      await (settingsQuery.value as MatchingWeekSettings).save();
      await guardDialog(() => syncEmailBannerToTemplates()());
      setSaveSuccess(true);
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e ?? { message: m.admin_email_settings_save_failed() }));
    } finally {
      setSaving(false);
    }
  };

  const uploadBannerFile = async (file: File) => {
    if (!settingsQuery.value) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const fileId = await ImageFile.upload(file);
      await handleBannerUpload(fileId);
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e ?? { message: m.admin_email_settings_save_failed() }));
    } finally {
      setSaving(false);
    }
  };

  const handleAdminEmailSave = async () => {
    if (!settingsQuery.value) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await (settingsQuery.value as MatchingWeekSettings).save();
      setSaveSuccess(true);
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e ?? { message: m.admin_email_settings_save_failed() }));
    } finally {
      setSaving(false);
    }
  };

  return html`
    <div class="p-4 sm:p-8 flex flex-col items-center">
      <div class="max-w-4xl w-full space-y-6">
        ${
          settingsQuery.loading
            ? html`<div class="flex justify-center py-8"><span class="loading loading-spinner loading-lg"></span></div>`
            : nothing
        }
        ${
          saveSuccess
            ? html`<div class="alert alert-success">
                <span class="icon-[material-symbols--check-circle]"></span
                ><span>${m.admin_email_settings_saved()}</span>
              </div>`
            : nothing
        }
        ${
          saveError
            ? html`<div class="alert alert-error">
                <span class="icon-[material-symbols--error]"></span><span>${saveError}</span>
              </div>`
            : nothing
        }
        ${
          settingsQuery.value
            ? html`
                <div class="card bg-base-100 shadow-xl">
                  <div class="card-body">
                    <h2 class="card-title text-2xl mb-4">${m.admin_email_settings_banner_title()}</h2>
                    <p class="text-sm text-base-content/70 mb-4">${m.admin_email_settings_banner_desc()}</p>

                    ${
                      settingsQuery.value.emailBannerFileId
                        ? html`
                            <div class="mb-4">
                              <img
                                src="${ImageFile.getServeLink(settingsQuery.value.emailBannerFileId)}"
                                alt="${m.admin_email_settings_banner_title()}"
                                class="w-full max-w-xl rounded border border-base-300"
                              />
                            </div>
                          `
                        : nothing
                    }

                    <div class="flex flex-wrap items-center gap-3">
                      ${FileInput({
                        accept: 'image/*',
                        placeholder: m.admin_email_settings_banner_placeholder(),
                        fileEntity: ImageFile,
                        value: settingsQuery.value.emailBannerFileId,
                        disabled: saving,
                        onFilesChanged: ({ pendingFiles }) => {
                          const file = pendingFiles[0];
                          if (file) void uploadBannerFile(file);
                        },
                      })}
                      ${
                        settingsQuery.value.emailBannerFileId
                          ? html`<button
                              class="btn btn-ghost btn-sm text-error"
                              ?disabled=${saving}
                              @click=${() => handleBannerUpload(null)}
                            >
                              <span class="icon-[material-symbols--delete]"></span>
                              ${m.admin_email_settings_remove_banner()}
                            </button>`
                          : nothing
                      }
                    </div>
                  </div>
                </div>

                <div class="card bg-base-100 shadow-xl">
                  <div class="card-body">
                    <h2 class="card-title text-2xl mb-4">${m.admin_email_settings_notification_title()}</h2>
                    <p class="text-sm text-base-content/70 mb-4">${m.admin_email_settings_notification_desc()}</p>
                    <div class="form-control">
                      <input
                        type="email"
                        class="input input-bordered w-full max-w-md"
                        placeholder="admin@example.com"
                        .value=${settingsQuery.value.adminNotificationEmail ?? ''}
                        @input=${(e: InputEvent) => {
                          if (settingsQuery.value)
                            settingsQuery.value.adminNotificationEmail = (e.target as HTMLInputElement).value;
                        }}
                      />
                    </div>
                    <div class="flex justify-end mt-4">
                      <button class="btn btn-primary" ?disabled=${saving} @click=${handleAdminEmailSave}>
                        <span class="icon-[material-symbols--save]"></span>
                        ${m.admin_email_settings_save()}
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
});
